/**
 * PDF font setup for @react-pdf/renderer.
 *
 * Body text uses built-in Times-Roman (WinAnsi). Math symbols (≥, ≤, etc.) use
 * embedded Noto Sans Math so clinical comparison operators render correctly.
 *
 * IMPORTANT: @fontsource/noto-sans's "latin" subset (noto-sans-latin-400-normal.woff)
 * does NOT contain glyphs for ≥ (U+2265), ≤ (U+2264), ≠ (U+2260), or ≈ (U+2248) —
 * verified with fontkit (font.hasGlyphForCodePoint returns false for all four). Embedding
 * a font that's missing the very glyph being requested causes PDFKit/fontkit's custom-font
 * subsetting to emit garbage (e.g. "≥" rendering as an unrelated letter, or corrupting
 * neighboring glyphs in the same subset) instead of a clean fallback. @fontsource/noto-sans-math
 * is the correct font for this — it actually contains the full Mathematical Operators block.
 */
import { Font } from '@react-pdf/renderer';
import notoSansMathRegular from '@fontsource/noto-sans-math/files/noto-sans-math-latin-400-normal.woff';

/** Built-in PDF serif — metrically equivalent to Times New Roman */
export const PDF_FONT_REGULAR = 'Times-Roman';
export const PDF_FONT_BOLD = 'Times-Bold';
export const PDF_FONT_ITALIC = 'Times-Italic';
export const PDF_FONT_BOLD_ITALIC = 'Times-BoldItalic';

/** Unicode fallback for math / comparison glyphs not in WinAnsi */
export const PDF_UNICODE_FONT = 'NotoSans';

/** @deprecated alias — use PDF_FONT_REGULAR */
export const PDF_BODY_FONT = PDF_FONT_REGULAR;

/** @deprecated use PDF_UNICODE_FONT */
export const PDF_SYMBOL_FONT = PDF_UNICODE_FONT;

/** @deprecated use PDF_FONT_REGULAR */
export const PDF_SERIF_FAMILY = PDF_FONT_REGULAR;

/**
 * Built-in PDF fonts are separate families — never use fontWeight:bold on Times-Roman.
 * PDFKit fake-bold can corrupt glyph coordinates ("unsupported number: -1.37e+22").
 */
export function pdfFontFamily({ bold = false, italic = false } = {}) {
  if (bold && italic) return PDF_FONT_BOLD_ITALIC;
  if (bold) return PDF_FONT_BOLD;
  if (italic) return PDF_FONT_ITALIC;
  return PDF_FONT_REGULAR;
}

export function sanitizePdfPt(value, fallback = 11, min = 6, max = 96) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

export function sanitizeLineHeight(value, fallback = 1.15) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0 || n > 4) return fallback;
  return n;
}

let registered = false;
let unicodeFontAvailable = false;

export function isPdfUnicodeFontAvailable() {
  return unicodeFontAvailable;
}

export async function registerPdfFonts() {
  if (registered) return unicodeFontAvailable;

  // Disable hyphenation so PDFKit never calls .split() on our callback with unexpected types.
  Font.registerHyphenationCallback((word) => [String(word ?? '')]);

  try {
    Font.register({
      family: PDF_UNICODE_FONT,
      src: notoSansMathRegular,
    });
    unicodeFontAvailable = true;
  } catch (err) {
    console.warn(
      'Noto Sans Math PDF font registration failed; comparison symbols will use ASCII fallbacks.',
      err,
    );
    unicodeFontAvailable = false;
  }

  registered = true;
  return unicodeFontAvailable;
}
